/**
 * This file is the primary data store of the application and contains methods
 * to fetch data from the backend and manipulate the current app's state.
 *
 * Data changes are propagated using "change:<attr>" events to the renderer.
 * In general you want to use the `useStore()` and `useStoreData(attr)` hooks.
 */

import React from "react"
import EventEmitter from "eventemitter3"

import * as consts from "./constants"
import parseDataXML, { mergeParserResults } from "./Parser"

export const StoreContext = React.createContext(null)

function getDefaultFilters() {
    return {
        showNetKe: true,
        showNetSf: true,
        showPowerQualityDrops: false,
        showStations: true,
        showGrosskunden: true,
        showKritinfra: true,
        showConstructionSites: true,
        searchText: "",
    }
}

/**
 * Returns default values for the DataStore.mapFocus attribute.
 */
function getDefaultMapFocus() {
    return {
        lat: consts.LAT_INIT,
        lng: consts.LNG_INIT,
        zoom: consts.ZOOM_INIT,
        marker: null, // {lat, lng, title}
    }
}

export default class DataStore extends EventEmitter {
    constructor() {
        super()

        this.interval = null

        // Filter settings
        this.filter = getDefaultFilters()
        this.hasFilterSet = false

        this.selectedElement = null
        this.mapFocus = getDefaultMapFocus()

        this.loading = true
        this.loadError = null

        this.variant = null
        this.rawData = {}
        this.viewState = {}
        this.versionInfo = {
            git_revision: process.env.REACT_APP_GIT_REVISION,
            time_epoch: process.env.REACT_APP_TIME_EPOCH,
            time_human: process.env.REACT_APP_TIME_HUMAN,
            time_iso: process.env.REACT_APP_TIME_ISO,
        }
        this.nextReload = null

        this.reload = this.reload.bind(this)
        this.setLoading = this.setLoading.bind(this)
        this.updateViewState = this.updateViewState.bind(this)

        this.on("change:rawData", this.updateViewState)
    }

    /**
     * Update data from the server.
     */
    async reload() {
        this.setLoading(true)

        let dataSources = [
            "data/stoerungen.xml",
            "data/construction-sites.xml"
        ]

        if (dataSources.length === 0) {
            this.rawData = {
                variant: "Public",
                updated: new Date(),
                outages: {},
                powerqualitydrops: {},
            }
            this.variant = this.rawData.variant
            this.setLoading(false)

            this.emit("change:rawData", this.rawData)
            this.emit("change:variant", this.variant)
            return
        }

        try {
            let loaders = dataSources.map(async url => {
                url +=
                    (url.indexOf("?") === -1 ? "?" : "") +
                    "_nocache=" +
                    ~~new Date()

                // console.log("[DataStore] Requesting data:", url)

                const rawXML = await fetch(url).then(result => result.text())
                return parseDataXML(rawXML)
            })

            let results = await Promise.all(loaders)
            let newData = mergeParserResults(results)

            this.rawData = newData
            this.variant = newData.variant

            this.emit("change:rawData", this.rawData)
            this.emit("change:variant", this.variant)

            if (!this.lastMapPosition) {
                const dataBoundaries = calculateMapBounds(newData)
                if (
                    dataBoundaries.centerLat && !isNaN( dataBoundaries.centerLat )
                    && dataBoundaries.centerLng && !isNaN( dataBoundaries.centerLng )
                ) {
                    this.updateMapFocus({
                        lat: dataBoundaries.centerLat,
                        lng: dataBoundaries.centerLng,
                    })
                }
            }
        } catch (e) {
            console.error(e)
            this.setLoadError(e.message || e)
        } finally {
            this.setLoading(false)
        }
    }

    /**
     * Update the focus of the map.
     * Compared to the other setProperty methods, this one takes a object
     * of changes and applies that to the focus.
     * This allows to only change the position or zoom level.
     */
    updateMapFocus(changes) {
        let newMapFocus = {
            ...this.mapFocus,
            ...changes,
        }

        this.mapFocus = newMapFocus
        this.emit("change:mapFocus", newMapFocus)
    }

    /**
     * Update the data source loading error.
     */
    setLoadError(newError) {
        this.loadError = newError
        this.emit("change:loadError", newError)
    }

    /**
     * Update the loading state.
     */
    setLoading(newLoading) {
        this.loading = newLoading
        this.emit("change:loading", newLoading)
    }

    /**
     * Calculates and updates the current viewState based on the rawData and filters.
     */
    updateViewState() {
        const { outages, powerqualitydrops, updated } = this.rawData

        const filtersAndSummaries = refreshFiltersAndSummaries(
            outages,
            powerqualitydrops,
            this.filter,
            this.filter.searchText,
        )

        const newViewState = {
            hasStations: outages["stations"] && outages["stations"].length > 0,
            hasGrosskunden:
                outages["grosskunden"] && outages["grosskunden"].length > 0,
            hasKritinfra:
                outages["kritinfras"] && outages["kritinfras"].length > 0,
            hasConstructionSites:
                outages["construction-sites"] &&
                outages["construction-sites"].length > 0,
            hasPowerQualityDrops:
                powerqualitydrops["grosskunden"] &&
                powerqualitydrops["grosskunden"].length > 0,

            visibleStations: filtersAndSummaries.visibleStations,
            visibleGrosskunden: filtersAndSummaries.visibleGrosskunden,
            visibleKritinfra: filtersAndSummaries.visibleKritinfra,
            visibleConstructionSites:
                filtersAndSummaries.visibleConstructionSites,
            visiblePowerQualities: filtersAndSummaries.visiblePowerQualities,
            summaries: filtersAndSummaries.summaries,
            sourcesUpdated: updated,

            selectedElement: this.selectedElement,

            filter: { ...this.filter },
            settings: {},

            lastRefresh: new Date(),
        }

        this.viewState = newViewState
        this.emit("change:viewState", newViewState)
    }
}

/**
 * Zentrales Update/Refresh alle Filter und Counter,
 * auf Basis eines Outage Objekts/Datensatzes.
 */
function refreshFiltersAndSummaries(outageObj, powerQualityDropsObj, filter) {
    let visibleStations = filterIncidents(outageObj.stations, filter)
    let visibleGrosskunden = filterIncidents(outageObj.grosskunden, filter)
    let visibleKritinfra = filterIncidents(outageObj.kritinfras, filter)
    let visibleConstructionSites = filterIncidents(
        outageObj["construction-sites"],
        filter,
    )
    let visiblePowerQualities = filterIncidents(
        powerQualityDropsObj["grosskunden"],
        filter,
    )

    let summaries = {
        ONS: visibleStations.length,
        AOs: 0,
        GPart: 0,
        GrossK: visibleGrosskunden.length,
        KritInfra: visibleKritinfra.length,
        ConstructionSites: visibleConstructionSites.length,
        PowerQualities: visiblePowerQualities.length,
    }

    visibleStations.forEach(incident => {
        if (incident.ao) {
            summaries["AOs"] += incident.ao
        }
        if (incident.gpart) {
            summaries["GPart"] += incident.gpart
        }
    })

    return {
        visibleStations: visibleStations,
        visibleGrosskunden: visibleGrosskunden,
        visibleKritinfra: visibleKritinfra,
        visibleConstructionSites: visibleConstructionSites,
        visiblePowerQualities: visiblePowerQualities,
        summaries: summaries,
    }
}

/**
 * Alle Ausfälle anhand des angegebenen Filters reduzieren.
 */
function filterIncidents(stack, filter) {
    let searchText = filter.searchText.toLowerCase()

    // Might happen if the data is empty/undef
    if (!Array.isArray(stack)) return []

    return stack.filter(obj => {
        let maybeVisible = false

        if (filter.showNetKe === true && obj.netId === consts.NET_ID_KE) {
            maybeVisible = true
        } else if (
            filter.showNetSf === true &&
            obj.netId === consts.NET_ID_SF
        ) {
            maybeVisible = true
        } else if (
            obj.netId === consts.NET_DEFAULT
        ) {
            maybeVisible = true
        }

        if (filter.searchText) {
            maybeVisible =
                JSON.stringify(obj)
                    .toLowerCase()
                    .indexOf(searchText) !== -1
        }

        return maybeVisible
    })
}

/**
 * Calculates map boundaries which contain all elements from the server data.
 *
 * @param newData {Object} Processed server data
 * @return {Object} minLat, maxLat, minLng, maxLng
 */
function calculateMapBounds(newData) {
    let sources = [
        newData.outages ? newData.outages.stations : null,
        newData.outages ? newData.outages.grosskunden : null,
        newData.outages ? newData.outages.kritinfras : null,
        newData.outages ? newData.outages["construction-sites"] : null,
        newData.powerqualitydrops,
    ]
    let latValues = []
    let lngValues = []

    for (let i = 0; i < sources.length; i++) {
        let source = sources[i]
        if (Array.isArray(source)) {
            source.forEach(incident => {
                if (incident.lat) {
                    latValues.push(incident.lat)
                }
                if (incident.lng) {
                    lngValues.push(incident.lng)
                }
            })
        }
    }

    // Math.min/max requires a list of arguments, and might return garbage.
    // JavaScript is a fun language
    let minLat = Math.min.apply(null, latValues) || consts.LAT_INIT
    let minLng = Math.min.apply(null, lngValues) || consts.LNG_INIT
    let maxLat = Math.max.apply(null, latValues) || consts.LAT_INIT
    let maxLng = Math.max.apply(null, lngValues) || consts.LNG_INIT

    // Add some padding to prevent the events to stick at the edges
    const latPadding = 0.22609293323927868 // km2lat(25)
    const lngPadding = 0.22457781169782087 // km2lng(25)
    minLat -= latPadding
    minLng -= lngPadding
    maxLat += latPadding
    maxLng += lngPadding

    const centerLat = maxLat - (maxLat - minLat) / 2
    const centerLng = maxLng - (maxLng - minLng) / 2

    return {
        minLat: minLat,
        minLng: minLng,
        maxLat: maxLat,
        maxLng: maxLng,

        centerLat: centerLat,
        centerLng: centerLng,
    }
}
